import React from 'react';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Target, Users, TrendingUp, CheckCircle2, Calendar, MessageCircle, GraduationCap, Award, Briefcase, Eye, Recycle, ArrowRight, Sparkles, BarChart3, Heart } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
export function ImpactPage() {
  return <Layout data-id="element-1435">
      <SEO {...pageSEO.impact} data-id="element-1436" />
      {/* Header */}
      <div className="bg-[#2C3E50] text-white py-16" data-id="element-1437">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1438">
          <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1 text-sm font-medium text-blue-200 mb-4" data-id="element-1439">
            Une association naissante, des ambitions concrètes
          </span>
          <h1 className="text-4xl font-bold mb-4" data-id="element-1440">Notre Vision d'Impact</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-1441">
            L'ADCC construit son impact pas à pas, avec transparence et rigueur.
            Découvrez notre point de départ, nos objectifs 2026 et comment nous
            mesurerons notre réussite.
          </p>
        </div>
      </div>

      {/* Notre Point de Départ - Le Forum Fondateur */}
      <section className="py-20 bg-white" data-id="element-1442">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1443">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1444">
            <div data-id="element-1445">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1446">
                NOTRE POINT DE DÉPART
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6" data-id="element-1447">
                Le Forum Fondateur
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" dangerouslySetInnerHTML={{
              __html: "Nos premiers pas ont été marqués par un <strong>forum d'état des lieux</strong>, qui a réuni une dizaine de personnels de maison de différents domaines pour cartographier leurs besoins et aspirations."
            }} data-id="element-1448" />

              <div className="bg-amber-50/50 rounded-r-xl p-6 border-l-4 border-amber-400 mb-8" data-id="element-1449">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1450">
                  <MessageCircle className="w-5 h-5 text-amber-500" data-id="element-1451" />
                  Ce que nous avons appris
                </h3>
                <ul className="space-y-3" data-id="element-1452">
                  <li className="flex items-start gap-3" data-id="element-1453">
                    <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1454" />
                    <span className="text-gray-700" data-id="element-1455">
                      Un besoin réel en compétences professionnelles
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1456">
                    <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1457" />
                    <span className="text-gray-700" data-id="element-1458">
                      Une demande forte de reconnaissance et de dignité
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1459">
                    <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1460" />
                    <span className="text-gray-700" data-id="element-1461">
                      L'importance d'un accompagnement vers l'emploi structuré
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1462">
                Ce forum a posé les bases de notre approche :{' '}
                <strong data-id="element-1463">écouter d'abord, agir ensuite</strong>. Chaque action de
                l'ADCC est construite à partir des besoins réels exprimés par
                les premiers concernés.
              </p>
            </div>
            <div className="relative" data-id="element-1464">
              <img src="https://cdn.magicpatterns.com/uploads/exRoSvgKroC4rrK6KpbESt/WhatsApp_Image_2026-01-29_at_17.21.09.jpg" alt="Forum fondateur ADCC" className="relative rounded-2xl shadow-xl w-full object-cover h-[400px]" data-id="element-1465" />
              {/* Forum Fondateur Badge */}
              <div className="absolute bottom-4 right-4 bg-white rounded-xl px-4 py-3 shadow-lg flex items-center gap-3" data-id="element-1466">
                <div className="p-2 bg-blue-50 rounded-full" data-id="element-1467">
                  <Calendar className="w-5 h-5 text-[#1E88E5]" data-id="element-1468" />
                </div>
                <div data-id="element-1469">
                  <p className="font-bold text-gray-900 text-sm" data-id="element-1470">
                    Forum Fondateur
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1471">2024 • Bénin</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Objectifs 2026 */}
      <section className="py-20 bg-gray-50" data-id="element-1472">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1473">
          <div className="text-center mb-16" data-id="element-1474">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1475">
              CAP SUR 2026
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1476">
              Nos Objectifs Chiffrés
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1477">
              Des ambitions réalistes et mesurables pour notre première année
              d'activité pleine.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8" data-id="element-1478">
            <Card className="text-center border-t-4 border-t-[#1E88E5] bg-white" data-id="element-1479">
              <CardContent className="pt-8 pb-6" data-id="element-1480">
                <div className="inline-flex items-center justify-center p-4 bg-blue-50 rounded-full mb-4" data-id="element-1481">
                  <GraduationCap className="w-8 h-8 text-[#1E88E5]" data-id="element-1482" />
                </div>
                <p className="text-5xl font-bold text-[#1E88E5] mb-2" data-id="element-1483">100</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1484">
                  Personnes à accompagner
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1485">
                  Via nos ateliers de Mars 2026 et sessions complémentaires
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-t-4 border-t-green-500 bg-white" data-id="element-1486">
              <CardContent className="pt-8 pb-6" data-id="element-1487">
                <div className="inline-flex items-center justify-center p-4 bg-green-50 rounded-full mb-4" data-id="element-1488">
                  <Briefcase className="w-8 h-8 text-green-600" data-id="element-1489" />
                </div>
                <p className="text-5xl font-bold text-green-600 mb-2" data-id="element-1490">60%</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1491">
                  Taux de placement visé
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1492">
                  Des bénéficiaires accompagnés insérés professionnellement
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-t-4 border-t-purple-500 bg-white" data-id="element-1493">
              <CardContent className="pt-8 pb-6" data-id="element-1494">
                <div className="inline-flex items-center justify-center p-4 bg-purple-50 rounded-full mb-4" data-id="element-1495">
                  <Heart className="w-8 h-8 text-purple-600" data-id="element-1496" />
                </div>
                <p className="text-5xl font-bold text-purple-600 mb-2" data-id="element-1497">100%</p>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1498">
                  D'insertion éthique
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1499">
                  Transparents et respectueux des droits
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Pourquoi ces objectifs ? */}
          <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm flex flex-col md:flex-row items-start gap-4 mb-16" data-id="element-1500">
            <div className="p-3 bg-blue-50 rounded-full flex-shrink-0" data-id="element-1501">
              <Target className="w-6 h-6 text-[#1E88E5]" data-id="element-1502" />
            </div>
            <div data-id="element-1503">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1504">
                Pourquoi ces objectifs ?
              </h3>
              <p className="text-gray-600" data-id="element-1505">
                Ces chiffres sont ambitieux mais réalistes pour une première
                année. Nous préférons des objectifs atteignables que nous
                pourrons dépasser, plutôt que des promesses impossibles à tenir.
                La crédibilité se construit dans la durée.
              </p>
            </div>
          </div>

          {/* Process Flow */}
          <div className="mt-16" data-id="element-1506">
            <div className="text-center mb-10" data-id="element-1507">
              <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1508">
                NOTRE APPROCHE
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2" data-id="element-1509">
                La Chaîne de Valeur ADCC
              </h3>
              <p className="text-gray-600 mt-2 max-w-2xl mx-auto" data-id="element-1510">
                Une approche complète, de la montée en compétences à l'emploi
                durable, pour un impact réel et mesurable.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 relative z-10" data-id="element-1511">
              {[{
              icon: GraduationCap,
              title: 'Développer',
              desc: 'Compétences techniques et humaines',
              color: 'bg-blue-50',
              iconColor: 'text-blue-600'
            }, {
              icon: Award,
              title: 'Tester',
              desc: 'Validation et professionnalisation',
              color: 'bg-green-50',
              iconColor: 'text-green-600'
            }, {
              icon: Briefcase,
              title: 'Insérer',
              desc: 'Mise en relation avec employeurs',
              color: 'bg-purple-50',
              iconColor: 'text-purple-600'
            }, {
              icon: Eye,
              title: 'Suivre',
              desc: 'Accompagnement post-placement',
              color: 'bg-amber-50',
              iconColor: 'text-amber-600'
            }, {
              icon: Recycle,
              title: 'Recycler',
              desc: 'Mise à jour des compétences',
              color: 'bg-teal-50',
              iconColor: 'text-teal-600'
            }].map((step, index) => <Card key={index} className="bg-white border-none shadow-md hover:shadow-lg transition-shadow relative" data-id="element-1512">
                  <CardContent className="p-6 text-center" data-id="element-1513">
                    <div className={`w-12 h-12 mx-auto rounded-full ${step.color} flex items-center justify-center mb-4`} data-id="element-1514">
                      <step.icon className={`w-6 h-6 ${step.iconColor}`} data-id="element-1515" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2" data-id="element-1516">
                      {step.title}
                    </h4>
                    <p className="text-xs text-gray-600" data-id="element-1517">{step.desc}</p>
                  </CardContent>
                </Card>)}
            </div>

            {/* Engagement Banner */}
            <div className="mt-12 bg-[#1E88E5] rounded-xl p-8 text-center text-white shadow-lg flex flex-col items-center justify-center gap-4" data-id="element-1518">
              <Sparkles className="w-8 h-8 text-yellow-300" data-id="element-1519" />
              <p className="text-xl md:text-2xl font-bold" data-id="element-1520">
                "Notre engagement : transformer les compétences en opportunités
                d'emploi durables."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mesure de l'Impact */}
      <section className="py-20 bg-white" data-id="element-1521">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1522">
          <div className="text-center mb-16" data-id="element-1523">
            <span className="text-[#1E88E5] font-semibold tracking-wide uppercase text-sm" data-id="element-1524">
              Transparence & Rigueur
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4" data-id="element-1525">
              Comment nous mesurerons notre impact
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1526">
              Des indicateurs clairs pour évaluer notre réussite et rendre
              compte à nos partenaires et bénéficiaires.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1527">
            {[{
            icon: Users,
            title: 'Nombre de bénéficiaires accompagnés',
            desc: 'Comptabilisation des participants ayant complété nos programmes de formation',
            target: 'Objectif 2026 : 100 personnes'
          }, {
            icon: Briefcase,
            title: "Taux d'insertion professionnelle",
            desc: 'Pourcentage de bénéficiaires de nos ateliers ayant trouvé un emploi dans les 6 mois',
            target: 'Objectif 2026 : 60%'
          }, {
            icon: TrendingUp,
            title: 'Durabilité des placements',
            desc: 'Suivi à 6 et 12 mois pour mesurer la stabilité des emplois obtenus',
            target: 'Suivi systématique prévu'
          }, {
            icon: BarChart3,
            title: 'Satisfaction des bénéficiaires',
            desc: 'Enquêtes régulières auprès des personnes formées et placées',
            target: 'Questionnaires post-formation et post-placement'
          }].map((metric, index) => <Card key={index} className="bg-white" data-id="element-1528">
                <CardContent className="p-6" data-id="element-1529">
                  <div className="flex items-start gap-4" data-id="element-1530">
                    <div className="p-3 bg-blue-50 rounded-lg text-[#1E88E5]" data-id="element-1531">
                      <metric.icon className="w-6 h-6" data-id="element-1532" />
                    </div>
                    <div data-id="element-1533">
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1534">
                        {metric.title}
                      </h3>
                      <p className="text-gray-600 mb-4" data-id="element-1535">{metric.desc}</p>
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700" data-id="element-1536">
                        <Target className="w-4 h-4 text-[#1E88E5]" data-id="element-1537" />
                        {metric.target}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>

          {/* Rendez-vous fin 2026 */}
          <div className="mt-12 bg-orange-50 border border-orange-100 rounded-xl p-6 flex flex-col md:flex-row items-start gap-4" data-id="element-1538">
            <div className="p-3 bg-orange-100 rounded-full flex-shrink-0" data-id="element-1539">
              <Calendar className="w-6 h-6 text-orange-600" data-id="element-1540" />
            </div>
            <div data-id="element-1541">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1542">
                Rendez-vous fin 2026
              </h3>
              <p className="text-gray-600" data-id="element-1543">
                Nous publierons notre premier rapport d'impact fin 2026, avec
                les données réelles et vérifiables. D'ici là, suivez notre
                actualité sur notre blog et nos réseaux sociaux pour des mises à
                jour régulières sur nos avancées.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2C3E50] text-white" data-id="element-1544">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1545">
          <h2 className="text-3xl font-bold mb-6" data-id="element-1546">Participez à notre impact</h2>
          <p className="text-xl text-gray-300 mb-10" data-id="element-1547">
            Votre soutien est essentiel pour nous aider à atteindre nos
            objectifs et transformer la vie de nos bénéficiaires.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1548">
            <Link to="/donate" data-id="element-1549">
              <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-1550">
                <Heart className="w-5 h-5" data-id="element-1551" />
                Faire un don
              </Button>
            </Link>
            <Link to="/partner" data-id="element-1552">
              <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white/10 hover:text-white gap-2" data-id="element-1553">
                <Briefcase className="w-5 h-5" data-id="element-1554" />
                Recruter un personnel
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>;
}